import { useState, type ReactNode } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PillButton } from './pill-button';
import { humanize } from './text';

export interface SummaryEntry {
  label: string;
  value: ReactNode;
}

/** Rows shown before the rest is folded away. The next row peeks out under a fade. */
const MAX_ROWS = 4;

const Row = ({ entry }: { entry: SummaryEntry }) => (
  <span className='grid grid-cols-[5rem_1fr] items-baseline gap-3 py-1'>
    <span className='text-xs leading-4 text-gray-800'>{entry.label}</span>
    <span className='min-w-0 pr-5 text-[13px] leading-4.5 wrap-break-word text-text-body'>{entry.value}</span>
  </span>
);

/**
 * The idle read-only view of an editable group: the first rows, the rest folded behind a
 * reveal, the whole block one click from its form. "Show more" is a separate button because
 * it cannot nest inside the edit button; the fold is masked so the fade holds over the hover tint.
 */
export const EditableSummary = ({
  entries,
  addLabel,
  onEdit,
}: {
  entries: SummaryEntry[];
  addLabel: string;
  onEdit: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  if (entries.length === 0) {
    return (
      <PillButton dashed onClick={onEdit}>
        <Plus className='size-3.5' />
        {addLabel}
      </PillButton>
    );
  }

  const shown = entries.slice(0, MAX_ROWS);
  const folded = entries.slice(MAX_ROWS);

  return (
    <div className='-mx-2 flex flex-col'>
      <button
        type='button'
        onClick={onEdit}
        className='group relative flex w-full flex-col rounded-lg px-2 py-1 text-left transition-colors hover:bg-gray-100/60'
      >
        <Pencil className='absolute top-2 right-2 size-3.5 text-gray-800 opacity-0 transition-opacity group-hover:opacity-100' />
        {shown.map((entry) => (
          <Row key={entry.label} entry={entry} />
        ))}
        {folded.length > 0 && (
          <span
            className={cn(
              'flex flex-col overflow-hidden transition-[max-height] duration-300 ease-out',
              // Collapsed, the next row peeks out under the mask; expanded, the cap clears every row.
              expanded ? 'max-h-[40rem]' : 'max-h-5 mask-b-from-25%'
            )}
          >
            {folded.map((entry) => (
              <Row key={entry.label} entry={entry} />
            ))}
          </span>
        )}
      </button>

      {folded.length > 0 && (
        <button
          type='button'
          onClick={() => setExpanded((prev) => !prev)}
          className='w-fit rounded-md px-2 py-1 text-left text-xs leading-4 font-medium text-gray-800 transition-colors hover:text-text-heading'
        >
          {expanded ? 'Show less' : `Show ${folded.length} more`}
        </button>
      )}
    </div>
  );
};

export const SummaryTag = ({ text }: { text: string }) => (
  <span className='inline-flex items-center rounded-full border border-gray-500 bg-gray-100 px-2 text-xs leading-4.5 text-text-body'>
    {humanize(text)}
  </span>
);
